import { useMemo } from 'react'
import {
  useHuddleDateFetch,
  type HuddleDateFetcher,
} from './useHuddleDateFetch'
import {
  getHuddleLeaderboard,
  type HuddleLeaderboardResult,
  getHuddleLeaderboardOverview,
} from '@services/api/dashboard/huddle.api'
import {
  mapHuddleLeaderboardRowsToCard,
  mapHuddleLeaderboardRowsToDetailTable,
  mapHuddleLeaderboardSummaryToMetrics,
  type HuddleLeaderboardCardRow,
  type HuddleLeaderboardDetailTableRow,
} from '@utils/dashboardHuddle.util'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'

export type UseHuddleLeaderboardOptions = {
  date: string
  enabled?: boolean
  queued?: boolean
}

export type UseHuddleLeaderboardResult = {
  data: HuddleLeaderboardResult | null
  loading: boolean
  error: string | null
  summaryMetrics: DashboardMetricItemData[]
  cardRows: HuddleLeaderboardCardRow[]
  detailRows: HuddleLeaderboardDetailTableRow[]
}

function useHuddleLeaderboardFromFetcher({
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseHuddleLeaderboardOptions & {
  fetcher: HuddleDateFetcher<HuddleLeaderboardResult>
}): UseHuddleLeaderboardResult {
  const { data, loading, error } = useHuddleDateFetch(date, fetcher, {
    enabled,
    queued,
    errorMessage: 'Failed to load leaderboard',
  })

  const summaryMetrics = useMemo(
    () => mapHuddleLeaderboardSummaryToMetrics(data?.summary),
    [data?.summary],
  )

  const cardRows = useMemo(
    () => mapHuddleLeaderboardRowsToCard(data?.providers),
    [data?.providers],
  )

  const detailRows = useMemo(
    () => mapHuddleLeaderboardRowsToDetailTable(data?.providers),
    [data?.providers],
  )

  return { data, loading, error, summaryMetrics, cardRows, detailRows }
}

export function useHuddleLeaderboard(
  options: UseHuddleLeaderboardOptions,
): UseHuddleLeaderboardResult {
  return useHuddleLeaderboardFromFetcher({
    ...options,
    fetcher: getHuddleLeaderboard,
  })
}
export function useHuddleLeaderboardOverview(
  options: UseHuddleLeaderboardOptions,
): UseHuddleLeaderboardResult {
  return useHuddleLeaderboardFromFetcher({
    ...options,
    fetcher: getHuddleLeaderboardOverview,
  })
}
